import { type FetchSurveysData, type FetchSurveysParams, type SurveyUser, useSettings } from '@capsicum/common';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { surveyApi } from '../api';

export type UseSurveysParams = [
	params: FetchSurveysParams,
	options?: Omit<UseQueryOptions<FetchSurveysData, unknown, Array<SurveyUser>>, 'queryFn' | 'queryKey'>
];

export const surveyKeysFactory = {
	all: ['surveys'] as const,
	filtered: ({
		partnerIds = [],
		...params
	}: { partnerIds?: Array<string> } & Omit<FetchSurveysParams, 'partnerIds[]'>) =>
		[...surveyKeysFactory.all, ...partnerIds, params] as const
};

/**
 *  Returns all surveys
 */
export const useSurveysQuery = (...args: UseSurveysParams) => {
	const [{ 'partnerIds[]': partnerIds = [], ...params }, options] = args;

	const { areSettingsLoading, settings } = useSettings();
	const { data, error, isLoading, refetch } = useQuery<FetchSurveysData, unknown, Array<SurveyUser>>({
		enabled: Boolean(partnerIds.length && !areSettingsLoading && settings?.isPollEnabled),
		queryFn: async () =>
			(await surveyApi).fetchSurveys({
				limit: 50,
				page: 1,
				'partnerIds[]': partnerIds,
				'statuses[]': ['opened', 'scheduled'],
				'types[]': ['engagement', 'integrity'],
				...params
			}),
		queryKey: surveyKeysFactory.filtered({ partnerIds, ...params }),
		select: res => res.data || [],
		...options
	});

	return { data, error, isLoading, refetch } as const;
};
